import moment from 'moment'
import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { Button, Modal, Select, Spinner, StopsCard } from '../../../components'
import { weekDaysByLanguage } from '../../../helpers'
import { connectRedux } from '../../../helpers/connectRedux'
import { images } from '../../../images'
import { IBookingScheduler, IFavoriteResponse } from '../../../models'
import { LiteralsService } from '../../../services'
import { addBookingSchedulerAction, editBookingSchedulerAction } from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledDiv from './styles'

interface IOwnProps {
  // exactly one of the two: a favorite to schedule, or the scheduler being edited
  favorite?: IFavoriteResponse
  bookingScheduler?: IBookingScheduler
  onClose: () => void
}

interface IProps extends IOwnProps, DispatchProp<AnyAction> {
  isLoading: boolean
}

interface IState {
  name: string
  startDate: string
  endDate: string
  repeatEvery: number
  weekDaysSelected: string[]
}

const EVERY_WEEK = 1
const EVERY_2_WEEKS = 2

class BookingSchedulerModal extends PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    const scheduler = props.bookingScheduler

    this.state = {
      name: scheduler?.name ?? '',
      // the inputs are native date fields, which speak YYYY-MM-DD
      startDate: scheduler?.startDate ? moment(scheduler.startDate).format('YYYY-MM-DD') : '',
      endDate: scheduler?.endDate ? moment(scheduler.endDate).format('YYYY-MM-DD') : '',
      repeatEvery: scheduler?.repeatEvery ?? 0,
      weekDaysSelected: scheduler?.daysOfWeek ?? []
    }
  }

  public render(): ReactNode {
    const { isLoading, onClose } = this.props
    const { name, startDate, endDate, repeatEvery } = this.state

    return (
      <Modal isHidden={ false } onOverlayClick={ onClose }>
        <StyledDiv>
          <header>
            <h3>{ LiteralsService.get('scheduled', true) }</h3>
            <button className="close" onClick={ onClose } aria-label={ LiteralsService.get('close', true) }>
              <img src={ images.cross } alt="close" />
            </button>
          </header>

          { this.trip }

          {
            isLoading
              ? <Spinner />
              : (
                <>
                  <div className="field">
                    <label htmlFor="scheduler-name">{ LiteralsService.get('putName', true) }</label>
                    <input
                      id="scheduler-name"
                      value={ name }
                      onChange={ event => this.setState({ name: event.target.value }) }
                    />
                  </div>

                  <div className="dates">
                    <div className="field">
                      <label htmlFor="scheduler-start">{ LiteralsService.get('bookingSchedulerStartDate', true) }</label>
                      <input
                        id="scheduler-start"
                        type="date"
                        className={ this.hasDateError ? 'error' : '' }
                        value={ startDate }
                        min={ moment().format('YYYY-MM-DD') }
                        onChange={ event => this.setState({ startDate: event.target.value }) }
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="scheduler-end">{ LiteralsService.get('bookingSchedulerEndDate', true) }</label>
                      <input
                        id="scheduler-end"
                        type="date"
                        className={ this.hasDateError ? 'error' : '' }
                        value={ endDate }
                        min={ startDate || moment().format('YYYY-MM-DD') }
                        onChange={ event => this.setState({ endDate: event.target.value }) }
                      />
                    </div>
                  </div>
                  { this.hasDateError && <p className="error-text">{ LiteralsService.get('bookingSchedulerDatesError', true) }</p> }

                  <div className="field">
                    <label>{ LiteralsService.get('bookingSchedulerRepeatEvery', true) }</label>
                    <Select
                      // Select renders whatever `value` holds, so it takes the label, not the id
                      value={ this.repeatOptions.find(option => option.value === String(repeatEvery))?.label ?? '' }
                      options={ this.repeatOptions }
                      placeholder={ LiteralsService.get('bookingSchedulerRepeatEvery', true) }
                      onChange={ value => this.setState({ repeatEvery: Number(value) }) }
                    />
                  </div>

                  { !!repeatEvery && this.weekDays }

                  <footer>
                    <Button
                      title={ LiteralsService.get('continue') }
                      styles={{ width: '220px' }}
                      onPress={ this.handleSave }
                      isDisabled={ this.isSaveDisabled }
                      filled
                      upper
                    />
                  </footer>
                </>
              )
          }
        </StyledDiv>
      </Modal>
    )
  }

  private get repeatOptions(): { value: string, label: string }[] {
    return [
      { value: String(EVERY_WEEK), label: LiteralsService.get('every_week', true) },
      { value: String(EVERY_2_WEEKS), label: LiteralsService.get('every_2_weeks', true) }
    ]
  }

  private get favorite(): IFavoriteResponse | undefined {
    return this.props.favorite ?? this.props.bookingScheduler?.favorite
  }

  private get trip(): ReactNode {
    const favorite = this.favorite
    if (!favorite) return null

    return (
      <div className="trip">
        <StopsCard
          inStopName={ favorite.inStop.properties.name }
          outStopName={ favorite.exitStop.properties.name }
        />
        <span className="when">
          { `${ LiteralsService.get('departure', true) } ${ moment(favorite.time, 'HH:mm:ss').format('HH:mm') }` }
        </span>
      </div>
    )
  }

  private get weekDays(): ReactNode {
    const locale = weekDaysByLanguage[LiteralsService.getLanguage()] || weekDaysByLanguage.en
    const english = weekDaysByLanguage.en.long

    return (
      <div className="week-days">
        {
          locale.short.map((day, index) => {
            const value = english[index]
            const isSelected = this.state.weekDaysSelected.includes(value)

            return (
              <button
                key={ value }
                type="button"
                className={ `day ${ isSelected ? 'selected' : '' }` }
                aria-pressed={ isSelected }
                onClick={ () => this.toggleWeekDay(value) }
              >
                { day }
              </button>
            )
          })
        }
      </div>
    )
  }

  private get hasDateError(): boolean {
    const { startDate, endDate } = this.state

    return !!startDate && !!endDate && moment(endDate).isBefore(moment(startDate), 'day')
  }

  private get isSaveDisabled(): boolean {
    const { name, startDate, endDate, repeatEvery, weekDaysSelected } = this.state

    return !name || !startDate || !endDate || !repeatEvery || !weekDaysSelected.length || this.hasDateError
  }

  private toggleWeekDay = (day: string): void => {
    const { weekDaysSelected } = this.state

    this.setState({
      weekDaysSelected: weekDaysSelected.includes(day)
        ? weekDaysSelected.filter(selected => selected !== day)
        : [...weekDaysSelected, day]
    })
  }

  private handleSave = (): void => {
    const { dispatch, bookingScheduler, onClose } = this.props
    const { name, startDate, endDate, repeatEvery, weekDaysSelected } = this.state
    const favorite = this.favorite
    if (!favorite) return

    const body = {
      id: bookingScheduler?.id,
      name,
      repeatEvery,
      daysOfWeek: weekDaysSelected,
      bookingTime: moment(favorite.time, 'HH:mm:ss').format('HH:mm'),
      favorite,
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD')
    } as unknown as IBookingScheduler

    dispatch(bookingScheduler ? editBookingSchedulerAction(body) : addBookingSchedulerAction(body))
    onClose()
  }
}

const mapStateToProps = ({ bookingScheduler }: IStoreState) => ({
  isLoading: bookingScheduler.isLoadingBookingScheduler
})

export default connectRedux(mapStateToProps, BookingSchedulerModal as any) as React.ComponentType<IOwnProps>
